import {Spin} from 'antd';
import Empty from 'antd/lib/empty';
import React, {
    useEffect,
    useImperativeHandle,
    useState,
    forwardRef,
} from 'react';

import {ModalOpenerComponent} from 'components/modal-opener-component';
import {
    getCustomLinkModalContentData,
    getCustomLinkTableData,
} from 'features/provide-custom-link/api/custom-link-modal.api';
import {ICustomLinkOpenModalRef, TCustomLinkModalTableColumn} from 'features/provide-custom-link/model/types';
import {TLinkDto, ELinkModalViewType} from 'shared/types/links';

import {CustomLinkModalTableView} from './content-views/table-view';
import s from './custom-link-modal.module.less';

interface ICustomLinkModalContentProps {
    link?: TLinkDto;
}

export const CustomLinkModalContent = ({
    link,
}: ICustomLinkModalContentProps) => {
    const {
        modalViewProperties,
    } = link ?? {};

    const {
        id,
        type,
    } = modalViewProperties ?? {};

    const [columns, setColumns] = useState<TCustomLinkModalTableColumn[] | undefined>(undefined);
    const [isColumnsLoaded, setIsColumnsLoaded] = useState(false);
    const [modalContentData, setModalContentData] = useState<unknown>(undefined);
    const [isContentLoaded, setIsContentLoaded] = useState(false);
    const [isLoadedWithError, setIsLoadedWithError] = useState(false);

    useEffect(() => {
        if (!id) {
            console.error('Ошибка: id для запроса данных таблицы модального окна не найден');
            return;
        }

        (async () => {
            await getCustomLinkTableData({
                id,
                setColumns,
                setIsError: setIsLoadedWithError,
                setIsLoaded: setIsColumnsLoaded,
            });
        })();
    }, []);

    useEffect(() => {
        const {
            url,
            method,
            bodyParameters,
            queryParameters,
            token,
        } = link ?? {};
        let isModalOpen = true;

        if (url) {
            (async () => {
                await getCustomLinkModalContentData({
                    url,
                    method,
                    bodyParameters,
                    queryParameters,
                    token,
                    isModalOpen,
                    setIsError: setIsLoadedWithError,
                    setIsLoaded: setIsContentLoaded,
                    setData: setModalContentData,
                });
            })();
        }

        return () => {
            isModalOpen = false;
        };
    }, []);

    if (!isColumnsLoaded) {
        return (
            <div className={s['custom-link-modal__spin']}>
                <Spin tip="Загрузка конфигурации таблицы..." />
            </div>
        );
    }

    if (!isContentLoaded) {
        return (
            <div className={s['custom-link-modal__spin']}>
                <Spin tip="Загрузка данных..." />
            </div>
        );
    }

    if (isLoadedWithError) {
        return (
            <div className={s['custom-link-modal__spin']}>
                Ошибка загрузки данных.
            </div>
        );
    }

    if (type === ELinkModalViewType.TABLE) {
        return (
            (() => {
                if (!columns) {
                    return (
                        <Empty
                            description="Отсутствуют столбцы таблицы"
                        />
                    );
                }

                return (
                    <CustomLinkModalTableView
                        columns={columns}
                        tableData={modalContentData as Record<string, string | number>[] | undefined}
                    />
                );
            })()
        );
    }

    return (
        <Empty
            description={`Компонент для типа ${type} не найден`}
        />
    );
};

interface ICustomLinkModalProps {
    link?: TLinkDto;
}

export const CustomLinkModal = forwardRef<ICustomLinkOpenModalRef, ICustomLinkModalProps>((
    {link: rootLink}: ICustomLinkModalProps, ref,
) => {
    const [isOpen, setOpen] = useState(false);
    const [currentLink, setCurrentLink] = useState<TLinkDto | undefined>(rootLink);

    useEffect(() => {
        setCurrentLink(rootLink);
    }, [rootLink]);

    // нужно для перехвата конфига link вместо родительского (rootLink),
    // когда конфиг по модалке вложенный (внутри children)
    // такое решение является альтернативой добавлению CustomLinkModal в каждый компонент CustomLinkView
    useImperativeHandle(ref, () => ({
        showModalWithLink: (newLink?: TLinkDto) => {
            if (newLink) {
                setCurrentLink(newLink); // Переписываем на новое значения, если передано
            }
            setOpen(true);
        },
    }));

    const {
        modalViewProperties,
    } = currentLink ?? {};

    const {name: modalTitle} = modalViewProperties ?? {};

    return (
        <ModalOpenerComponent
            isOpen={isOpen}
            setIsOpen={setOpen}
            modalProps={{
                forceRender: false,
                destroyOnClose: true,
                centered: true,
                width: 1480,
                title: modalTitle ?? currentLink?.position?.title ?? 'Просмотр данных',
            }}
            controlLabels={{
                cancel: 'Закрыть',
            }}
        >
            <CustomLinkModalContent link={currentLink} />
        </ModalOpenerComponent>
    );
});
